import { isWebinarId } from '@core/shared';
import { EmailTemplate, FollowUp, OptionsApi } from '@core/swagger-client';
import {
    Checkbox,
    CompositePageHeader,
    ContainerNoTopSpace,
    ContainerWebinarInner,
    ContentHeader,
    EmailPreviewPanel,
    ErrorMessage,
    ErrorMessagePage,
    Heading,
    Input,
    LoaderPage,
    PageHeader,
    PageHeaderContent,
    TextArea
} from '@core/ui';
import { useParams } from '@online-webinar/router';
import { BackLink } from '@online-webinar/ui-frontend';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

export const FollowUpPage = () => {
    const { t } = useTranslation();
    const [saveState, setSaveState] = useState<'saved' | 'saving' | 'error'>('saved');

    const [followUp, setFollowUp] = useState<{ followUp: FollowUp; emailTemplate: EmailTemplate; version: number }>();
    const [savedFollowUp, setSavedFollowUp] = useState<{ followUp: FollowUp; emailTemplate: EmailTemplate; version: number }>();

    const [renderedEmailTemplate, setRenderedEmailTemplate] = useState<EmailTemplate>();

    const { webinarId, followUpId } = useParams({ strict: false });
    if (!isWebinarId(followUpId)) return <ErrorMessagePage statusCode={404} />;

    const { data: webinar, error: webinarError, isLoading: webinarIsLoading } = useQuery(OptionsApi.webinar.get(webinarId!, { staleTime: 60_000 }));
    const {
        data: followUpData,
        error: followUpError,
        isLoading: followUpIsLoading
    } = useQuery(OptionsApi.followUp.getById({ webinarId: webinarId!, followUpId: followUpId! }, { refetchOnWindowFocus: false }));

    useEffect(() => {
        if (!followUpData || followUp) return;

        setFollowUp({ followUp: { ...followUpData.followUp }, emailTemplate: { ...followUpData.emailTemplate }, version: 0 });
        setSavedFollowUp({ followUp: { ...followUpData.followUp }, emailTemplate: { ...followUpData.emailTemplate }, version: 0 });
        setRenderedEmailTemplate(followUpData.renderedEmailTemplate);

        setSaveState('saved');
    }, [followUpData, followUp]);

    const addOrUpdateMutation = useMutation(OptionsApi.followUp.addOrUpdate());
    useEffect(() => {
        if (!followUp || !savedFollowUp || webinarIsLoading || followUpIsLoading) {
            return;
        }

        if (JSON.stringify(followUp) === JSON.stringify(savedFollowUp)) {
            return;
        }

        // Make a copy of the follow-up that we are saving to avoid mutating the original state
        const saving = { followUp: { ...followUp.followUp }, emailTemplate: { ...followUp.emailTemplate }, version: followUp.version };
        const handle = setTimeout(() => {
            addOrUpdateMutation.mutate(
                { webinarId: webinarId!, followUp: saving },
                {
                    onSuccess: (data) => {
                        setSaveState('saved');
                        setSavedFollowUp(saving);
                        setRenderedEmailTemplate(data.renderedEmailTemplate);
                    },
                    onError: (error) => {
                        setSaveState('error');
                    }
                }
            );
        }, 1000);
        return () => clearTimeout(handle);
    }, [followUp, savedFollowUp]);

    const error = webinarError || followUpError;
    if (error) {
        return <ErrorMessagePage apiError={error} />;
    }

    if (!webinar || !followUp || webinarIsLoading || followUpIsLoading) {
        return <LoaderPage></LoaderPage>;
    }

    return (
        <>
            <ContainerNoTopSpace>
                <PageHeader>
                    <PageHeaderContent>
                        <CompositePageHeader title={webinar.title} broadcastDateTime={webinar.broadcastDateTime} />
                    </PageHeaderContent>
                </PageHeader>
                <ContainerWebinarInner>
                    <BackLink to="/$webinarId/follow-up" params={{ webinarId }}>
                        {t('Button.Back')}
                    </BackLink>
                    <div>
                        {saveState === 'saved' && <p className="text-success mb-4">{t('EditFollowUp.Toast.Saved')}</p>}
                        {saveState === 'saving' && <p className="text-orange-500 mb-4">{t('EditFollowUp.Toast.Saving')}</p>}
                        {saveState === 'error' && <p className="text-warning mb-4">{t('EditFollowUp.Toast.Error')}</p>}
                    </div>
                    <ContentHeader contentHeader={t('EditFollowUp.PageHeading')} />
                    {addOrUpdateMutation.error && (
                        <div className="inline-flex items-center bg-warning-light rounded-md text-sm mb-6 pl-3 pr-4 py-2">
                            <ErrorMessage apiError={addOrUpdateMutation.error} />
                        </div>
                    )}
                    <div className="max-w-lg">
                        <Input
                            classNames="mb-6"
                            label={t('EditFollowUp.Label.Name')}
                            value={followUp.followUp.title}
                            onChange={(e) =>
                                setFollowUp({
                                    followUp: { ...followUp.followUp, title: e.target.value },
                                    emailTemplate: followUp.emailTemplate,
                                    version: followUp.version + 1
                                })
                            }
                        />
                        <Checkbox
                            id="bccOwner"
                            label={t('EditFollowUp.Label.BccOwner')}
                            checked={followUp.followUp.bccOwner}
                            onChange={(e) =>
                                setFollowUp({
                                    followUp: { ...followUp.followUp, bccOwner: e.target.checked },
                                    emailTemplate: followUp.emailTemplate,
                                    version: followUp.version + 1
                                })
                            }
                            classNames="mb-6"
                        />
                        <Input
                            classNames="mb-6"
                            label={t('EditFollowUp.Label.MinimumOnlinePercentage')}
                            value={followUp.followUp.minimumOnlinePercentage}
                            type="number"
                            onChange={(e) =>
                                setFollowUp({
                                    followUp: { ...followUp.followUp, minimumOnlinePercentage: parseFloat(e.target.value) },
                                    emailTemplate: followUp.emailTemplate,
                                    version: followUp.version + 1
                                })
                            }
                        />
                        <Input
                            classNames="mb-6"
                            label={t('EditFollowUp.Label.MinimumPollPoints')}
                            value={followUp.followUp.minimumPollPoints}
                            type="number"
                            onChange={(e) =>
                                setFollowUp({
                                    followUp: { ...followUp.followUp, minimumPollPoints: parseInt(e.target.value) },
                                    emailTemplate: followUp.emailTemplate,
                                    version: followUp.version + 1
                                })
                            }
                        />
                        <Input
                            classNames="mb-6"
                            label={t('EditFollowUp.Label.MinimumPollVotes')}
                            value={followUp.followUp.minimumPollVotes}
                            type="number"
                            onChange={(e) =>
                                setFollowUp({
                                    followUp: { ...followUp.followUp, minimumPollVotes: parseInt(e.target.value) },
                                    emailTemplate: followUp.emailTemplate,
                                    version: followUp.version + 1
                                })
                            }
                        />
                        <Input
                            classNames="mb-6"
                            label={t('EditFollowUp.Label.MinimumExamPoints')}
                            value={followUp.followUp.minimumExamPoints}
                            type="number"
                            onChange={(e) =>
                                setFollowUp({
                                    followUp: { ...followUp.followUp, minimumExamPoints: parseInt(e.target.value) },
                                    emailTemplate: followUp.emailTemplate,
                                    version: followUp.version + 1
                                })
                            }
                        />
                        <Input
                            classNames="mb-6"
                            label={t('EditFollowUp.Label.MinimumExamAnswers')}
                            value={followUp.followUp.minimumExamAnswers}
                            type="number"
                            onChange={(e) =>
                                setFollowUp({
                                    followUp: { ...followUp.followUp, minimumExamAnswers: parseInt(e.target.value) },
                                    emailTemplate: followUp.emailTemplate,
                                    version: followUp.version + 1
                                })
                            }
                        />
                        <Input
                            classNames="mb-6"
                            label={t('EditFollowUp.Label.MinimumSurveyAnswers')}
                            value={followUp.followUp.minimumSurveyAnswers}
                            type="number"
                            onChange={(e) =>
                                setFollowUp({
                                    followUp: { ...followUp.followUp, minimumSurveyAnswers: parseInt(e.target.value) },
                                    emailTemplate: followUp.emailTemplate,
                                    version: followUp.version + 1
                                })
                            }
                        />
                    </div>
                </ContainerWebinarInner>
            </ContainerNoTopSpace>
            <div className="flex flex-col md:flex-row flex-1">
                <div className="bg-white flex-1 px-4 pb-4 pt-12 md:pt-24">
                    <div className="sticky top-4 max-w-140 mx-auto">
                        <Heading variant="h2" classNames="mt-3 mb-6">
                            {t('EditFollowUp.Heading.EmailTemplate')}
                        </Heading>
                        <div className="max-w-lg">
                            <Input
                                classNames="mb-6"
                                label={t('Email.Template.Label.Subject')}
                                value={followUp.emailTemplate.subject}
                                onChange={(e) =>
                                    setFollowUp({
                                        followUp: followUp.followUp,
                                        emailTemplate: { ...followUp.emailTemplate, subject: e.target.value },
                                        version: followUp.version + 1
                                    })
                                }
                            />
                        </div>
                        <div className="max-w-lg">
                            <TextArea
                                label={t('Email.Template.Label.Body')}
                                value={followUp.emailTemplate.body}
                                onChange={(e) =>
                                    setFollowUp({
                                        followUp: followUp.followUp,
                                        emailTemplate: { ...followUp.emailTemplate, body: e.target.value },
                                        version: followUp.version + 1
                                    })
                                }
                                textAreaClassNames="min-h-[40vh] field-sizing-content"
                            ></TextArea>
                        </div>
                    </div>
                </div>
                <div className="bg-info bg-center bg-cover bg-no-repeat flex-1 flex flex-col items-center px-4 pt-4 md:pt-16 xl:pt-12 pb-6">
                    <EmailPreviewPanel
                        subject={renderedEmailTemplate?.subject || ''}
                        html={renderedEmailTemplate?.body || ''}
                        isLoading={saveState === 'saving'}
                    />
                </div>
            </div>
        </>
    );
};
